import { BeneficiaryVisibilityType, Paths } from "@/modules/beneficiary/common/enum";
import { Beneficiaries, BeneficiaryDetails } from "@/modules/beneficiary/components";
import { getBeneficiaryTabItems } from "@/modules/beneficiary/helpers";
import { useGetBeneficiariesQuery } from "@/modules/beneficiary/services";
import { INITIAL_PAGE, INITIAL_PAGE_SIZE } from "@/shared/common/constants";
import { CloudflareAsset } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, PageHeader, ScrollableTab } from "@/shared/components";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { setActiveBeneficiaryId } from "@/shared/state/common";
import { debounce } from "@/shared/utils";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const BeneficiariesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const activeItem = useAppSelector(state => state.common);

  const [selectedTab, setSelectedTab] = useState<string>(
    activeItem?.beneficiaryActiveTab ||
    BeneficiaryVisibilityType.Private
  );
  const [searchValue, setSearchValue] = useState<string>("");
  const [activeId, setActiveId] = useState<string | null>(null);

  const { data: beneficiaries, isFetching, isError, error, refetch } = useGetBeneficiariesQuery(
    { type: selectedTab, name: searchValue, pageIndex: INITIAL_PAGE, pageSize: INITIAL_PAGE_SIZE },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (activeItem.beneficiaryId) {
      setActiveId(`${activeItem.beneficiaryId}`);
    }
  }, [activeItem.beneficiaryId]);

  const updateDebouncedPage = useCallback(
    debounce((value: string) => {
      setSearchValue(value);
    }, 700),
    []
  );

  return (
    <>
      <div className="full-width page-container beneficiaries">
        <PageHeader>
          {!activeId && (
            <div className="beneficiaries__header-actions">
              <Input
                leftIconProps={{
                  Component: (props) => (
                    <CloudFlareImage {...props} path={CloudflareAsset.Search}/>
                  )
                }}
                onChange={(e) => updateDebouncedPage(e.target.value)}
                placeholder={t("beneficiary.search.placeholder")}
                className="mr-16 header-search-input"
              />
              <Button
                buttonText={t("beneficiary.newBeneficiary.title")}
                data-testid="add-beneficiary"
                data-ga-id="add-beneficiary"
                data-ga-action="Navigate to create beneficiary page"
                onClick={() => navigate(Paths.CreateBeneficiary)}
                iconProps={{
                  Component: (props: ISVGIconProps) => (
                    <CloudFlareImage {...props} path={CloudflareAsset.AddLight}/>
                  ),
                }}
              />
            </div>
          )}
        </PageHeader>
        <div className="full-width flexbox justify-content--center h-fit-content page-body">
          <div className="widget-wrapper">
            <div className="full-width mb-20">
              <Heading className="content-title mb-20" size="xsmall" weight="bolder">
                {t("beneficiary.title")}
              </Heading>
            </div>
            <ScrollableTab
              activeTab={activeItem.beneficiaryActiveTab}
              tabEnum={BeneficiaryVisibilityType}
              selectedTab={selectedTab}
              setSelectedTab={(tab) => {
                setActiveId(null);
                dispatch(setActiveBeneficiaryId(null));
                setSelectedTab(tab);
              }}
              getTabItems={getBeneficiaryTabItems}
            />
            <Beneficiaries
              beneficiaryName={searchValue}
              type={selectedTab}
              activeId={activeId ?? undefined}
              beneficiaries={beneficiaries}
              isFetching={isFetching}
              isError={isError}
              error={error as IError}
              refetch={refetch}
              onItemClick={(beneficiary) => setActiveId(beneficiary.id)}
              onDeleteItem={(id) => {
                if (activeId === id) {
                  setActiveId(null);
                  dispatch(setActiveBeneficiaryId(null));
                }
              }}
            />
          </div>
        </div>
      </div>
      {activeId && (
        <BeneficiaryDetails
          isOpen
          onClose={() => {
            setActiveId(null);
            dispatch(setActiveBeneficiaryId(null));
          }}
          beneficiaryId={activeId}
          beneficiaryType={selectedTab}
          refetchBeneficiariesList={refetch}
        />
      )}
    </>
  );
};
